import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsChevronDown } from 'react-icons/bs';
import {
  Box,
  Flex,
  Icon,
  Menu, MenuButton, MenuItem, MenuList,
  Text
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import colors from '../../styles/theme/foundation/colors';
import { hoverStyle } from '../MultiSelectDropdown/styles';
import SearchInputGroup from '../SearchInputGroup';
import TruncationTooltip from '../TruncationTooltip';

import AddCustomInput from './AddCustomInput';

const DropdownMenu = ({
  options = [],
  variant = 'primary',
  width = '300px',
  size = 'md',
  selected,
  placeholder = 'None selected',
  labelKey = 'label',
  idKey = 'id',
  isDisabled = false,
  dataAnalyticsKey = undefined,
  allowCustom = false,
  customPlaceholder = '',
  customLabel = 'Add New',
  customValidation = () => {},
  onCustomSelect = () => {},
  onSelect = () => {},
  useOriginal = false,
  isSearchable = false,
  searchPlaceholder = 'Search'
}) => {
  const initialItems = useMemo(() => options.map(item => ({
    value: item[idKey],
    label: item[labelKey],
    dataAnalyticsId: item?.dataAnalyticsId || (
      dataAnalyticsKey ? `${dataAnalyticsKey}-item` : undefined
    ),
    rightIcon: item?.rightIcon || null,
    leftIcon: item?.leftIcon || null,
    original: item
  })), [options, selected]);

  const selectedDisplay = useMemo(() => {
    if (selected && useOriginal && typeof selected === 'object') return selected[labelKey];
    if (selected && useOriginal && typeof selected === 'string') return selected;
    if (selected && !useOriginal) return selected;
    return placeholder;
  }, [selected, useOriginal, labelKey, placeholder]);

  const [items, setItems] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef();
  const menuListRef = useRef();
  const onOpen = () => setOpen(true);
  const onClose = () => {
    setOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Menu moves focus to whichever item the pointer is over (and to the list when it leaves),
  // which would yank focus out of the search input mid-typing.
  const keepFocusInSearch = ({ target }) => {
    if (target !== searchInputRef.current) searchInputRef.current?.focus({ preventScroll: true });
  };

  const visibleItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;

    return items.filter(item => String(item.label ?? '').toLowerCase().includes(term));
  }, [items, searchTerm]);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]); // eslint-disable-line

  const handleSelect = original => ({ target }) => {
    onSelect(useOriginal ? original : { label: target.name, value: target.value });
    onClose();
  };

  const highlight = index => {
    setHighlightedIndex(index);
    menuListRef.current
      ?.querySelector(`[data-option-index="${index}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  };

  // Focus stays in the input, so the input drives the highlight instead of Menu's own focus management.
  const handleSearchKeyDown = e => {
    if (e.key === 'Escape') return;
    e.stopPropagation();

    const count = visibleItems.length;
    if (e.key === 'ArrowDown' && count) {
      e.preventDefault();
      highlight((highlightedIndex + 1) % count);
    } else if (e.key === 'ArrowUp' && count) {
      e.preventDefault();
      highlight(highlightedIndex <= 0 ? count - 1 : highlightedIndex - 1);
    } else if (e.key === 'Enter' && visibleItems[highlightedIndex]) {
      e.preventDefault();
      const item = visibleItems[highlightedIndex];
      onSelect(useOriginal ? item.original : { label: item.label, value: String(item.value) });
      onClose();
    }
  };

  return (
    <Menu
      closeOnSelect
      initialFocusRef={isSearchable ? searchInputRef : undefined}
      isOpen={isOpen}
      size={size}
      variant={variant}
      onClose={onClose}
      onOpen={onOpen}
    >
      <MenuButton
        _hover={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
        data-analytics-id={dataAnalyticsKey}
        disabled={isDisabled}
        dusk={dataAnalyticsKey}
        minW={width}
        width={width}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          if (isDisabled) return;
          isOpen ? onClose() : onOpen();
        }}
      >
        <Flex alignItems="center" justifyContent="space-between" pointerEvents="none" width="100%">
          <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" width="95%">
            <TruncationTooltip label={selectedDisplay}>
              <Text
                _active={variant === 'primary' && { color: colors.primaryScale['600'] }}
                color={variant === 'primary' && colors.greyScale['900']}
                overflow="hidden"
                pointerEvents="auto"
                textOverflow="ellipsis"
                textStyle={selected ? 'bodyMd500' : 'bodyMd'}
              >
                {selectedDisplay}
              </Text>
            </TruncationTooltip>
          </Box>
          <Icon as={BsChevronDown} />
        </Flex>
      </MenuButton>
      <MenuList
        maxH="300px"
        minW={width}
        pt={isSearchable ? 0 : undefined}
        ref={menuListRef}
        width={width}
        onFocus={isSearchable ? keepFocusInSearch : undefined}
      >
        {isSearchable && (
          <Box
            bg="white"
            pb={2}
            position="sticky"
            pt={2}
            px={2}
            top={0}
            zIndex={1}
            onKeyDown={handleSearchKeyDown}
          >
            <SearchInputGroup
              placeHolder={searchPlaceholder}
              ref={searchInputRef}
              searchTerm={searchTerm}
              variant="primary"
              onChange={e => {
                setSearchTerm(e.target.value);
                setHighlightedIndex(e.target.value.trim() ? 0 : -1);
              }}
            />
          </Box>
        )}
        {isSearchable && items.length > 0 && !visibleItems.length && (
          <Text color={colors.greyScale['500']} px={3} py={2} textStyle="bodyMd">No results</Text>
        )}
        {items.length
          ? visibleItems.map((item, index) => (
            <TruncationTooltip key={item.value} label={item.label}>
              <MenuItem
                _focus={{ ...hoverStyle, outline: 'none' }}
                _selected={hoverStyle}
                color={item.label === selected ? colors.primaryScale['600'] : colors.greyScale['600']}
                data-analytics-id={item?.dataAnalyticsId}
                data-option-index={isSearchable ? index : undefined}
                display="flex"
                dusk={`${item?.dataAnalyticsId}-${item.value}`}
                icon={item?.leftIcon && <Icon as={item?.leftIcon} />}
                id={item.value}
                justifyContent="space-between"
                name={item.label}
                value={item.value}
                width="100%"
                onClick={handleSelect(item.original)}
                onMouseEnter={isSearchable ? () => setHighlightedIndex(index) : undefined}
                {...(isSearchable && index === highlightedIndex ? hoverStyle : {})}
              >
                <Text noOfLines={1} pointerEvents="none">
                  {item.label}
                </Text>
                {item.rightIcon && <Icon as={item?.rightIcon} />}
              </MenuItem>
            </TruncationTooltip>
          ))
          : <MenuItem>None</MenuItem>}

        {allowCustom && (
          <AddCustomInput
            buttonLabel={customLabel}
            customValidation={customValidation}
            placeholder={customPlaceholder}
            onClose={onClose}
            onCustomSelect={onCustomSelect}
          />
        )}
      </MenuList>
    </Menu>
  );
};

/** Dropdown menu to be used for data selection */
DropdownMenu.propTypes = {
  /** Array of objects to be mapped into menu items */
  options: PropTypes.arrayOf(PropTypes.shape({})),
  /** Variant for dropdown menu button */
  variant: PropTypes.oneOf(['primary', 'secondary']),
  /** Width for the menu button */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Dropdown menu can either be medium (default) or large */
  size: PropTypes.oneOf(['md', 'lg']),
  /** Currently selected item to be displayed on the dropdown menu button */
  selected: PropTypes.string,
  /** Text to be displayed if no option is selected */
  placeholder: PropTypes.string,
  /** Key to access text to be displayed on menu item,
   * i.e. if the text is accessed through the displayName property, displayName is the labelKey */
  labelKey: PropTypes.string,
  /** Key to access value/id of each menu item,
   * i.e. if the id is accessed through the value property, value is the idKey */
  idKey: PropTypes.string,
  /** True if menu is disabled */
  isDisabled: PropTypes.bool,
  /** Event triggered by selecting a menu item. Will generally include a state change for the selected item */
  onSelect: PropTypes.func,
  /** Optional, data attribute to track analytics */
  dataAnalyticsKey: PropTypes.string,
  /** True if user can enter a custom value */
  allowCustom: PropTypes.bool,
  /** Placeholder for custom value input */
  customPlaceholder: PropTypes.string,
  /** Text to be displayed on button to add a custom value */
  customLabel: PropTypes.string,
  /** Validation object if validation is needed on the custom input */
  customValidation: PropTypes.shape({}),
  /** Callback function that operates on or uses the custom input value */
  onCustomSelect: PropTypes.func,
  /** Flag to set whether to pass the original object to the onSelect handler or not, added for backwards compat. */
  useOriginal: PropTypes.bool,
  /** True to show a search input that filters the options by label */
  isSearchable: PropTypes.bool,
  /** Placeholder for the search input */
  searchPlaceholder: PropTypes.string
};

export default DropdownMenu;
